"use strict";
// Shared formatting utilities for Fomo-Fi web and mobile.
// Pure functions with no platform-specific dependencies.
Object.defineProperty(exports, "__esModule", { value: true });
exports.roundMoney = roundMoney;
exports.moneySign = moneySign;
exports.formatCurrency = formatCurrency;
exports.formatCurrencyCompact = formatCurrencyCompact;
exports.formatDate = formatDate;
exports.formatShortDate = formatShortDate;
exports.formatMonthYear = formatMonthYear;
exports.formatPercentage = formatPercentage;
exports.formatRelativeTime = formatRelativeTime;
exports.truncate = truncate;
exports.capitalize = capitalize;
exports.snakeToTitleCase = snakeToTitleCase;
/**
 * A money figure rounded to `digits` places, ties away from zero (BEN-141 FP-005).
 *
 * Hermes (iOS) formats with ties to EVEN at every precision: measured on an
 * iPhone 17, `(50.5).toLocaleString("en-US", {maximumFractionDigits: 0})` is
 * "50" and 51.5 is "52"; 0.125 to two places is "0.12", and `Intl.NumberFormat`
 * currency is the same. V8 (web, Node) rounds ties away from zero, so the two
 * apps disagreed by a dollar or a cent on any figure ending in exactly half.
 * Round first, then format: the formatter then never sees a tie.
 *
 * `toFixed` is exact on the stored double and takes the larger candidate on a
 * tie, on both engines, which is V8 Intl's `halfExpand` for the magnitude. On V8
 * this is therefore the identity for every `Intl`/`toLocaleString` money call.
 *
 * Exact on the DOUBLE, not on the decimal you typed: 1.005 is stored as
 * 1.00499…, so it rounds to 1.00 here, exactly as V8's Intl does.
 *
 * A figure that rounds to zero is +0, never −0 (CALL-06): `Intl` prints −0 with its
 * minus, so −0.004 at cents came out "-$0.00".
 */
function roundMoney(value, digits = 0) {
    if (!Number.isFinite(value))
        return value;
    const rounded = Number(Math.abs(value).toFixed(digits));
    if (rounded === 0)
        return 0;
    return value < 0 ? -rounded : rounded;
}
/**
 * The sign to print before a money figure's magnitude (CALL-06, report 164). Empty
 * when the figure shows as zero at `digits` places, so zero is "$0" / "$0.00" in every
 * signed variant: never "−$0", never "+$0". That covers 0, −0 and anything that rounds
 * to zero (−0.004 at cents, −0.4 in whole dollars).
 *
 * `plus: ""` for lines that sign only negatives; `minus` is the glyph the caller prints
 * ("-", or U+2212 "−").
 */
function moneySign(amount, digits = 0, { plus = "+", minus = "-" } = {}) {
    if (!Number.isFinite(amount) || roundMoney(amount, digits) === 0)
        return "";
    return amount < 0 ? minus : plus;
}
/**
 * Format a number as currency.
 * @param amount - The numeric amount
 * @param currency - ISO 4217 currency code (default: "USD")
 * @param locale - BCP 47 locale string (default: "en-US")
 */
function formatCurrency(amount, currency = "USD", locale = "en-US") {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(roundMoney(amount, 2));
}
/**
 * Format a number as compact currency (e.g., $1.2K, $3.4M).
 */
function formatCurrencyCompact(amount, currency = "USD", locale = "en-US") {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(roundMoney(amount, 1) === 0 ? 0 : amount);
}
/**
 * Format a date string or Date object to a long date (e.g., "Jan 15, 2025").
 */
function formatDate(date, locale = "en-US") {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(d);
}
/**
 * Format a date string or Date object to a short date (e.g., "Jan 15").
 */
function formatShortDate(date, locale = "en-US") {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
    }).format(d);
}
/**
 * Format a date string or Date object to month/year (e.g., "January 2025").
 */
function formatMonthYear(date, locale = "en-US") {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "long",
    }).format(d);
}
/**
 * Format a percentage value (e.g., 0.75 → "75.0%").
 * @param value - Value between 0 and 1 (or pass isRaw: true for 0–100)
 */
function formatPercentage(value, decimals = 1, isRaw = false) {
    const pct = isRaw ? value : value * 100;
    return `${pct.toFixed(decimals)}%`;
}
/**
 * Return a relative time string (e.g., "2 days ago", "in 3 hours").
 */
function formatRelativeTime(date, locale = "en-US") {
    const d = typeof date === "string" ? new Date(date) : date;
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffSeconds = Math.round(diffMs / 1000);
    const diffMinutes = Math.round(diffSeconds / 60);
    const diffHours = Math.round(diffMinutes / 60);
    const diffDays = Math.round(diffHours / 24);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (Math.abs(diffSeconds) < 60)
        return rtf.format(diffSeconds, "second");
    if (Math.abs(diffMinutes) < 60)
        return rtf.format(diffMinutes, "minute");
    if (Math.abs(diffHours) < 24)
        return rtf.format(diffHours, "hour");
    return rtf.format(diffDays, "day");
}
/**
 * Truncate a string to a max length, appending ellipsis if truncated.
 */
function truncate(str, maxLength) {
    if (str.length <= maxLength)
        return str;
    return `${str.slice(0, maxLength - 1)}…`;
}
/**
 * Capitalize the first letter of a string.
 */
function capitalize(str) {
    if (!str)
        return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
}
/**
 * Convert a snake_case string to Title Case.
 */
function snakeToTitleCase(str) {
    return str
        .split("_")
        .map((word) => capitalize(word))
        .join(" ");
}
//# sourceMappingURL=format.js.map